import { ApiError } from "@/lib/api";

export interface ErrorResponse {
  message: string;
  shouldRedirect?: boolean;
  redirectTo?: string;
  statusCode?: number;
}

/**
 * Handles API errors and returns user-friendly messages
 */
export function handleApiError(error: unknown): string {
  // Type guard for ApiError
  if (isApiError(error)) {
    return getErrorMessage(error);
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || "Beklenmeyen bir hata oluştu";
  }

  // Fallback for unknown error types
  return "Beklenmeyen bir hata oluştu";
}

/**
 * Get detailed error response with redirect information
 */
export function getDetailedError(error: unknown): ErrorResponse {
  if (isApiError(error)) {
    const message = getErrorMessage(error);
    const statusCode = error.statusCode;

    // Handle authentication errors
    if (statusCode === 401) {
      return {
        message,
        shouldRedirect: true,
        redirectTo: '/login',
        statusCode
      };
    }

    return { message, statusCode };
  }

  return {
    message: error instanceof Error ? error.message : "Beklenmeyen bir hata oluştu"
  };
}

/**
 * Type guard for ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof (error as ApiError).statusCode === 'number'
  );
}

/**
 * Get appropriate error message based on status code
 */
function getErrorMessage(error: ApiError): string {
  // Use backend message if available
  if (error.message && error.message !== "Bir hata oluştu") {
    return error.message;
  }

  // Fallback messages based on status code
  switch (error.statusCode) {
    case 400:
      return "Geçersiz istek. Lütfen girdiğiniz bilgileri kontrol edin.";
    case 401:
      return "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.";
    case 403:
      return "Bu işlem için yetkiniz bulunmuyor.";
    case 404:
      return "İstediğiniz kayıt bulunamadı.";
    case 409:
      return "Bu kayıt zaten mevcut.";
    case 422:
      return "Girdiğiniz bilgiler geçersiz.";
    case 429:
      return "Çok fazla istek gönderdiniz. Lütfen bir süre bekleyin.";
    case 500:
      return "Sunucu hatası. Lütfen daha sonra tekrar deneyin.";
    case 503:
      return "Servis şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.";
    default:
      return "Bir hata oluştu. Lütfen tekrar deneyin.";
  }
}

/**
 * Check if error is permission related
 */
export function isPermissionError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.statusCode === 403;
  }
  return false;
}

/**
 * Check if error is authentication related
 */
export function isAuthError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.statusCode === 401;
  }
  return false;
}

/**
 * Check if error is not found
 */
export function isNotFoundError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.statusCode === 404;
  }
  return false;
}
